import React, {useEffect, useState, useCallback} from "react";
import {Button, Grid, Paper, Typography, CircularProgress} from "@mui/material";
import {SectionTitle} from "../../../export/advanced_export";
import {MappingHeader} from "../advanced_supplier_import";
import {isListTypeAttribute} from "../profile_helpers";
import NavigationButtons from "./NavigationButtons";
import cloneDeep from 'lodash/cloneDeep';
import ListMappingItem from "./components/ListMappingItem";
import Fuse from 'fuse.js';
import MappingReviewDialog from "../../../../components/Dialogs/MappingReviewDialog";
import {aiMapAttributes} from "../../../../services/ai_service";
import {AI_MAPPING_ENABLED} from "../../../../../../utils/utils";
import EnhancedSnackbar from "../../../../../../components/EnhancedSnackbar";
import { pageContentGap } from "../../../../../../theme";
import type { WizardSharedProps } from "../types";

// region TypeScript Interfaces
type ListMappingProps = WizardSharedProps;

interface ListInfo {
    Name: string;
    Options: string[];
}

interface ListMappingSuggestion {
    supplierAttribute: string;
    retailerAttribute: string;
    confidence: number;
    source: string;
    listName: string;
    supplierValue: string;
}

interface SnackbarState {
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
}
// endregion

export default function ListMapping(props: ListMappingProps) {
    const {allowBack, allowNext, backStep, nextStep, activeStep, steps, setSteps} = props;
    const {profile, setProfile, formattedProfile, profileType} = props;
    const {parsedFieldsWithData} = props;
    const {retailerAttributes} = props;
    const {refreshView} = props;
    const {columnDataLoading} = props;

    const [mappedListItems, setMappedListItems] = React.useState<ListInfo[]>([]);
    const [mappedListItemOptions, setMappedListItemOptions] = React.useState<Record<string, string[]>>({});
    const [allListMappingExpanded, setAllListMappingExpanded] = React.useState<boolean>(false);

    // AI Mapping states
    const [aiMappingInProgress, setAiMappingInProgress] = useState<boolean>(false);
    const [mappingReviewDialogOpen, setMappingReviewDialogOpen] = useState<boolean>(false);
    const [mappingSuggestions, setMappingSuggestions] = useState<ListMappingSuggestion[]>([]);
    const [processingStep, setProcessingStep] = useState<number | null>(null);
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });
    const [applyingMappings, setApplyingMappings] = useState<boolean>(false);

    const autoMatch = async () => {

        setAiMappingInProgress(true);
        setMappingReviewDialogOpen(true);
        setProcessingStep(0);

        const allSuggestions: ListMappingSuggestion[] = [];

        // Process each list attribute
        for (const list of mappedListItems) {
            const supplierValues = mappedListItemOptions[list.Name] || [];
            const retailerValues = list.Options || [];


            // Skip if no values to map
            if (supplierValues.length === 0 || retailerValues.length === 0) continue;

            const unmappedValues: string[] = [];

            // Phase 1: Exact match (case-insensitive)
            const retailerLookup = new Map(
                retailerValues.map(value => [
                    value.toLowerCase(),
                    value
                ])
            );

            supplierValues.forEach(supplierValue => {
                // Skip if already mapped
                if ((profile.ListMappings?.[list.Name] as any)?.[supplierValue]) return;

                const supplierLower = supplierValue.toLowerCase();
                const exactMatch = retailerLookup.get(supplierLower);

                if (exactMatch) {
                    allSuggestions.push({
                        supplierAttribute: `${list.Name}: ${supplierValue}`,
                        retailerAttribute: exactMatch,
                        confidence: 1.0,
                        source: 'Fuzzy',
                        listName: list.Name,
                        supplierValue: supplierValue
                    });
                } else {
                    unmappedValues.push(supplierValue);
                }
            });

            // Phase 2: Fuzzy matching for unmapped
            if (unmappedValues.length > 0) {
                const fuseOpts = {
                    includeScore: true,
                    threshold: 0.3
                };
                const fuse = new Fuse(retailerValues, fuseOpts);

                const stillUnmatched: string[] = [];
                unmappedValues.forEach(supplierValue => {
                    const result: any = fuse.search(supplierValue);
                    if (result.length > 0 && result[0].score < 0.3) {
                        allSuggestions.push({
                            supplierAttribute: `${list.Name}: ${supplierValue}`,
                            retailerAttribute: result[0].item,
                            confidence: 1 - result[0].score,
                            source: 'Fuzzy',
                            listName: list.Name,
                            supplierValue: supplierValue
                        });
                    } else {
                        stillUnmatched.push(supplierValue);
                    }
                });

                setProcessingStep(1);

                // Phase 3: AI mapping for still unmapped (if enabled)
                if (AI_MAPPING_ENABLED() && stillUnmatched.length > 0) {
                    try {
                        const aiResponse: any = await aiMapAttributes(
                            stillUnmatched,
                            retailerValues
                        );

                        if (aiResponse.mappings) {
                            Object.entries(aiResponse.mappings as Record<string, any>).forEach(([supplierVal, mapping]) => {
                                allSuggestions.push({
                                    supplierAttribute: `${list.Name}: ${supplierVal}`,
                                    retailerAttribute: mapping.suggested,
                                    confidence: mapping.confidence,
                                    source: mapping.source,
                                    listName: list.Name,
                                    supplierValue: supplierVal
                                });
                            });
                        }
                    } catch (error) {
                        console.error('AI list mapping failed:', error);
                    }
                }
            }
        }

        setProcessingStep(2);
        setMappingSuggestions(allSuggestions);
        setAiMappingInProgress(false);
    };

    const handleApplyMappings = useCallback((acceptedMappings: ListMappingSuggestion[]) => {
        setApplyingMappings(true);

        const newMappings: Record<string, Record<string, string>> = { ...(profile.ListMappings as any) };

        acceptedMappings.forEach(mapping => {
            if (!newMappings[mapping.listName]) {
                newMappings[mapping.listName] = {};
            }
            // Map retailer attribute (key) to supplier value (value)
            newMappings[mapping.listName][mapping.retailerAttribute] = mapping.supplierValue;
        });

        setProfile(prev => {
            const updatedProfile = {
                ...prev,
                ListMappings: newMappings
            };
            return updatedProfile;
        });

        // Calculate totals for the success message
        const mappingCount = acceptedMappings.length;
        let totalPossibleMappings = 0;
        mappedListItems.forEach(list => {
            const supplierValues = mappedListItemOptions[list.Name] || [];
            totalPossibleMappings += supplierValues.length;
        });
        const unmappedCount = Math.max(0, totalPossibleMappings - mappingCount);

        // Show success message
        let message = `Successfully applied ${mappingCount} list value mapping${mappingCount !== 1 ? 's' : ''}`;
        if (unmappedCount > 0) {
            message += `. ${unmappedCount} value${unmappedCount !== 1 ? 's' : ''} remain unmapped.`;
        }

        setSnackbar({
            open: true,
            message,
            severity: 'success'
        });

        setApplyingMappings(false);
        setMappingReviewDialogOpen(false);
        setMappingSuggestions([]);
    }, [profile.ListMappings, setProfile, mappedListItems, mappedListItemOptions]);

    const onNext = () => {
        setSteps((prevState) => {
            let newState = cloneDeep(prevState);
            newState[activeStep].status = "success";
            return newState;
        });
    }

    const renderListMapping = React.useMemo(() => {

        return (
            <Grid container style={{width: "100%"}}>
                {mappedListItems.map((list, index) => (
                    <ListMappingItem
                        open={allListMappingExpanded}
                        key={index} list={list} profile={profile}
                        setProfile={setProfile}
                        mappedListItemOptions={mappedListItemOptions}/>
                    )
                )}
            </Grid>
        );

        // mappedListItemOptions dep so the memo recomputes when the deferred fetch fills values.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshView, JSON.stringify(mappedListItems), allListMappingExpanded, profile.ListMappings, mappedListItemOptions])

    useEffect(() => {
        // No lists to map, if no attributes mapped
        if (Object.keys(profile.AttributeMappings || {}).length !== 0) {
            const findListItems = Object.keys(profile.AttributeMappings || {}).filter(key =>
                isListTypeAttribute(retailerAttributes.find((attribute: any) => attribute.Name === key))
            );

            // No lists to map, if no attributes mapped are of type select
            if (findListItems.length !== 0) {
                let mappedListItemOptions: Record<string, string[]> = {};
                const mappedListItems = findListItems.map(listItem => {
                    let retailerAttribute: any = retailerAttributes.find((attribute: any) => attribute.Name === listItem);

                    let foundParsedField: any = parsedFieldsWithData.find((field: any) => field.field === profile.AttributeMappings[listItem]);
                    if (foundParsedField !== undefined)
                        mappedListItemOptions[listItem] = foundParsedField.values;

                    if (retailerAttribute.Type === "list") {
                        if (!retailerAttribute.ValidationRules)
                            return {Name: retailerAttribute.Name, Options: []};

                        let inListOptions: string[] = [];
                        const foundInListOptions = retailerAttribute.ValidationRules.filter((rule: any) => rule.Type === "containedin");

                        if (foundInListOptions && foundInListOptions.length > 0)
                            inListOptions = foundInListOptions[0].Parameters.value.split(",");

                        return {Name: retailerAttribute.Name, Options: inListOptions};
                    } else
                        return {Name: retailerAttribute.Name, Options: retailerAttribute.Options || []};
                });

                // Remove mappedListItems without any options
                let filteredMappedListItems = mappedListItems.filter((list: ListInfo) => list.Options.length !== 0);

                setMappedListItemOptions(mappedListItemOptions);
                setMappedListItems(filteredMappedListItems);
            } else {
                setMappedListItems((prev) => (prev.length ? [] : prev));
                setMappedListItemOptions((prev) => (Object.keys(prev).length ? {} : prev));
            }
        } else {
            setMappedListItems((prev) => (prev.length ? [] : prev));
            setMappedListItemOptions((prev) => (Object.keys(prev).length ? {} : prev));
        }

        // parsedFieldsWithData + retailerAttributes so options recompute when they load late.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshView, profile.AttributeMappings, parsedFieldsWithData, retailerAttributes]);

    return (
        <Grid container style={{gap: pageContentGap, width: "100%"}}>
            <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                <Grid container style={{flexDirection: "column", alignItems: "flex-start", width: "100%", gap: "16px"}}>
                    <Grid container style={{width: "100%", gap: "5px", flexDirection: "column", alignItems: "flex-start"}}>
                        <SectionTitle>
                            Product List Mapping
                        </SectionTitle>

                        <Grid container justifyContent={"flex-end"} style={{flex: 1}}>
                            <Button variant="outlined"
                                    onClick={() => {
                                        setAllListMappingExpanded(!allListMappingExpanded);
                                    }}
                            >
                                Toggle Sections
                            </Button>
                        </Grid>
                    </Grid>

                    {columnDataLoading ? (
                        <Grid container direction="column" justifyContent="center" alignItems="center"
                              style={{width: "100%", minHeight: "200px", gap: "12px"}}
                              data-testid="column-data-loading">
                            <CircularProgress size={48} />
                            <Typography variant="h6" color="textSecondary">Loading column values…</Typography>
                        </Grid>
                    ) : mappedListItems.length === 0 ? (
                        <Grid container justifyContent={"center"} alignItems={"center"} style={{height: "100%"}}>
                            <Typography>
                                No lists to map, if this is incorrect please check your attribute mappings.
                            </Typography>
                        </Grid>
                    ) : (
                        <Grid container style={{width: "100%"}}>
                            <Grid container item size={6} alignItems={"center"} style={{gap: pageContentGap}}>
                                <MappingHeader>
                                    Your Lists
                                </MappingHeader>
                                <Button variant="outlined" color="primary"
                                        style={{
                                            height: "32px",
                                            lineHeight: 1,
                                            minWidth: "120px"
                                        }}
                                        onClick={() => autoMatch()}
                                        disabled={aiMappingInProgress || mappedListItems.length === 0}
                                        startIcon={aiMappingInProgress ? <CircularProgress size={16} /> : null}
                                >
                                    {aiMappingInProgress ? "Matching..." : (AI_MAPPING_ENABLED() ? "Auto Match (AI)" : "Auto Match")}
                                </Button>
                            </Grid>

                            <Grid container item size={6} alignItems={"center"} style={{gap: pageContentGap}}>
                                <MappingHeader>
                                    Retailer Lists
                                </MappingHeader>
                            </Grid>

                            {renderListMapping}
                        </Grid>
                    )}
                </Grid>
            </Paper>
            {mappingReviewDialogOpen && (
                <MappingReviewDialog
                    open={mappingReviewDialogOpen}
                    onClose={() => {
                        setMappingReviewDialogOpen(false);
                        setMappingSuggestions([]);
                        setProcessingStep(null);
                    }}
                    mappingSuggestions={mappingSuggestions as any}
                    onApply={handleApplyMappings as any}
                    isProcessing={aiMappingInProgress}
                    processingStep={processingStep}
                    mappingType="lists"
                    isApplying={applyingMappings}
                />
            )}
            <EnhancedSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
            <NavigationButtons
                allowBack={allowBack}
                allowNext={allowNext}
                loading={columnDataLoading}
                backStep={backStep}
                nextStep={nextStep}
                onNext={onNext}
                activeStep={activeStep}
                steps={steps}

                formattedProfile={formattedProfile}
                profileType={profileType}
            />
        </Grid>
    );
}
